import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Download, FileText, Gift, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FreeBeatModal({ beat, open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [claimId, setClaimId] = useState(null);

  const reset = () => {
    setName("");
    setEmail("");
    setClaimId(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleClaim = async () => {
    if (!email.trim()) return toast.error("Please enter your email");
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/beats/${beat.id}/claim-free`, {
        name: name.trim(),
        email: email.trim(),
      });
      setClaimId(data.claim_id);
      toast.success("Your free beat is ready!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim free beat");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-background border-border" data-testid="free-beat-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Gift className="w-6 h-6 text-primary" />
            {claimId ? "Enjoy Your Free Beat!" : `Free Download: "${beat?.title}"`}
          </DialogTitle>
        </DialogHeader>

        {!claimId ? (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              This beat is free this month! Enter your details to download it and your license agreement.
            </p>
            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="rounded-xl bg-card"
                data-testid="free-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="rounded-xl bg-card"
                data-testid="free-email-input"
              />
            </div>
            <Button
              onClick={handleClaim}
              disabled={loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90 neon-glow gap-2"
              data-testid="claim-free-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
              Get It Free
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-4" data-testid="free-success">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">Download your files below — save them now.</span>
            </div>
            <a href={`${BACKEND_URL}/api/download/beat/${claimId}`} data-testid="free-download-beat">
              <Button className="w-full rounded-xl bg-primary hover:bg-primary/90 neon-glow gap-2">
                <Download className="w-4 h-4" /> Download Beat
              </Button>
            </a>
            <a href={`${BACKEND_URL}/api/download/license/${claimId}`} data-testid="free-download-license">
              <Button variant="outline" className="w-full rounded-xl gap-2">
                <FileText className="w-4 h-4" /> License Agreement (PDF)
              </Button>
            </a>
            <Button variant="ghost" onClick={handleClose} className="w-full rounded-xl">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
